/**
 * 🔐 ENCRYPTION API SERVICE
 * Handles communication with backend for encryption-related operations
 */

import { API_BASE_URL } from "../config/apiConfig";
import { createAuthAxios, handleApiError } from "../utils/apiUtils";

const ENCRYPTION_BASE = `${API_BASE_URL}/encryption`;

const encryptionApiService = {
  /**
   * Get a user's public key from backend
   * @param {string} userId - User ID
   * @returns {Promise<string>} User's public key (base64)
   */
  getPublicKey: async (userId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${ENCRYPTION_BASE}/public-key/${userId}`
      );

      if (response.data.success) {
        return response.data.data.publicKey;
      }

      throw new Error("Failed to fetch public key");
    } catch (error) {
throw handleApiError(error);
    }
  },

  /**
   * Batch fetch multiple public keys
   * @param {string[]} userIds - Array of user IDs
   * @returns {Promise<Object>} Map of userId -> publicKey
   */
  getPublicKeys: async (userIds) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(`${ENCRYPTION_BASE}/public-keys`, {
        userIds,
      });

      if (response.data.success) {
        // Convert array to map
        const keysMap = {};
        response.data.data.keys.forEach((k) => {
          keysMap[k.userId] = k.publicKey;
        });
        return keysMap;
      }

      throw new Error("Failed to fetch public keys");
    } catch (error) {
throw handleApiError(error);
    }
  },

  /**
   * Upload user's public key to backend
   * @param {string} publicKey - User's public key (base64)
   * @returns {Promise<Object>} Upload confirmation
   */
  uploadPublicKey: async (publicKey) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${ENCRYPTION_BASE}/upload-public-key`,
        { publicKey }
      );

      return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  },

  /**
   * Generate new encryption keys on backend
   * @returns {Promise<Object>} Generated keys
   */
  generateKeys: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${ENCRYPTION_BASE}/generate-keys`
      );

      return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  },

  /**
   * Rotate encryption keys
   * @param {string} newPublicKey - New public key
   * @returns {Promise<Object>} Rotation confirmation
   */
  rotateKeys: async (newPublicKey) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(`${ENCRYPTION_BASE}/rotate-keys`, {
        newPublicKey,
      });

      return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  },

  /**
   * Get key rotation status
   * @returns {Promise<Object>} Rotation status
   */
  getRotationStatus: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${ENCRYPTION_BASE}/rotation-status`
      );

      return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  },
};

export default encryptionApiService;

